/**
 * EbookAssetCard Component
 * =========================
 * Displays uploaded eBook with integrity badge, preservation status, and actions.
 */

import { useState } from 'react';
import { 
  FileText, 
  BookOpen, 
  Book, 
  Shield, 
  ShieldCheck, 
  Lock, 
  Eye, 
  Download, 
  Trash2, 
  RefreshCw,
  Copy,
  Check,
  Clock,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import ebookService from '@/services/ebookService';

const FILE_ICONS = {
  '.pdf': FileText,
  '.epub': BookOpen,
  '.mobi': Book
};

const PRESERVATION_COLORS = {
  'UPLOADED': 'bg-blue-100 text-blue-800 border-blue-200',
  'VERIFIED': 'bg-green-100 text-green-800 border-green-200',
  'ENCRYPTED': 'bg-purple-100 text-purple-800 border-purple-200',
  'FAILED': 'bg-red-100 text-red-800 border-red-200'
};

const PRESERVATION_ICONS = {
  'UPLOADED': Shield,
  'VERIFIED': ShieldCheck,
  'ENCRYPTED': Lock,
  'FAILED': AlertCircle
};

export default function EbookAssetCard({ 
  asset, 
  submissionId,
  onDelete,
  onReplace,
  onOpenReader,
  showAdminActions = false,
  onVerify
}) {
  const [checksumCopied, setChecksumCopied] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  
  const FileIcon = FILE_ICONS[asset.ext] || FileText;
  const PreservationIcon = PRESERVATION_ICONS[asset.preservation_status] || Shield;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const handleCopyChecksum = async () => {
    try {
      await navigator.clipboard.writeText(asset.checksum_sha256);
      setChecksumCopied(true);
      toast.success('Checksum copied to clipboard');
      setTimeout(() => setChecksumCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await ebookService.deleteEbook(submissionId, asset.id);
      toast.success('eBook deleted');
      if (onDelete) onDelete(asset.id);
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleDownload = () => {
    const token = localStorage.getItem('access_token');
    const url = ebookService.getDownloadUrl(asset.id);
    
    // Create a temporary link with auth header via fetch
    fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = asset.original_filename || asset.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('Download failed'));
  };
  
  const canPreview = asset.ext === '.pdf' || asset.ext === '.epub';
  
  return (
    <div 
      data-testid="ebook-asset-card"
      className="p-4 bg-white border-2 border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-4">
        {/* Preview Thumbnail / Icon */}
        <div className="flex-shrink-0 w-16 h-20 rounded-lg bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 flex items-center justify-center overflow-hidden">
          {asset.preview_available && !previewError ? (
            <img
              src={ebookService.getPreviewUrl(asset.id)}
              alt="Preview"
              className="w-full h-full object-cover rounded-lg"
              onError={() => setPreviewError(true)}
            />
          ) : (
            <FileIcon className="w-8 h-8 text-purple-600" />
          )}
        </div>
        
        {/* File Info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-slate-900 truncate" title={asset.original_filename}>
            {asset.original_filename || asset.filename}
          </h4>
          
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
            <span>{ebookService.formatFileSize(asset.size_bytes)}</span>
            <span>•</span>
            <span className="uppercase">{asset.ext?.replace('.', '')}</span>
            <span>•</span>
            <Clock className="w-3 h-3" />
            <span>{formatDate(asset.created_at)}</span>
          </div>
          
          {/* Badges Row */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {/* File Integrity Badge */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleCopyChecksum}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200 transition-colors"
                  >
                    {checksumCopied ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <ShieldCheck className="w-3 h-3" />
                    )}
                    SHA-256 ✓ {ebookService.formatChecksum(asset.checksum_sha256)}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs font-mono break-all">{asset.checksum_sha256}</p>
                  <p className="text-xs text-slate-400 mt-1">Click to copy</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Preservation Status */}
            <Badge 
              className={`${PRESERVATION_COLORS[asset.preservation_status]} border`}
            >
              <PreservationIcon className="w-3 h-3 mr-1" />
              {asset.preservation_status}
            </Badge>
            
            {/* Verified timestamp */}
            {asset.verified_at && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-green-600">
                      Verified {formatDate(asset.verified_at)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Admin verified at {formatDate(asset.verified_at)}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col gap-2">
          {canPreview && (
            <Button
              size="sm"
              onClick={() => onOpenReader && onOpenReader(asset)}
              className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white"
              data-testid="open-reader-btn"
            >
              <Eye className="w-4 h-4 mr-1" />
              Open Reader
            </Button>
          )}
          
          {!canPreview && (
            <Button
              size="sm"
              onClick={handleDownload}
              variant="outline"
              data-testid="download-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              Download
            </Button>
          )}
          
          <div className="flex gap-1">
            {onReplace && (
              <Button
                size="sm"
                variant="outline"
                onClick={onReplace}
                className="flex-1"
                title="Replace file"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            )}
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50 flex-1"
                  disabled={isDeleting}
                  title="Delete file"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete eBook?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete "{asset.original_filename}". This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          
          {/* Admin Actions */}
          {showAdminActions && asset.preservation_status === 'UPLOADED' && (
            <Button
              size="sm"
              onClick={() => onVerify && onVerify(asset)}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="verify-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-1" />
              Verify
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
